import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link, useLocation, useRoute } from 'wouter'
import { FileText, Search, Plus, Bookmark, BookmarkCheck, Code, Palette, Building, Heart, GraduationCap, Home, Globe, Users, Target, Zap, Shield, Cloud, Database, Brain, Link as LinkIcon, Smartphone, TrendingUp, Scale, UserCheck, Settings, Truck, ShoppingCart, Megaphone, MousePointer, PenTool, Monitor, Eye, Share2, Video, Camera, Music, Mic, Radio, BookOpen, School, FileBox, Stethoscope, Dumbbell, Apple, BrainCircuit, Building2, Hammer, Ruler, Sofa, Calendar, Plane, Languages, Search as SearchIcon, Leaf, HeartHandshake, Building as BuildingIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/auth-context'
import toast from 'react-hot-toast'
import { buildApiUrl } from '@/config/api'

interface Rfp {
  id: string
  title: string
  slug: string
  description: string
  status: string
  category: string
  budgetMin: number
  budgetMax: number
  budgetCurrency: string
  deadline: string
  views: number
  responses: number
  linkedinShares: number
  createdAt: string
}

export function AllRfps() {
  const { user } = useAuth()
  const [, setLocation] = useLocation()
  const [, params] = useRoute('/request-for-proposal-marketplace/:category')
  const [rfps, setRfps] = useState<Rfp[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [watchlistStatus, setWatchlistStatus] = useState<Record<string, boolean>>({})
  const [watchlistCache, setWatchlistCache] = useState<Record<string, boolean>>({})
  const [lastWatchlistCheck, setLastWatchlistCheck] = useState<number>(0)

  // Read category from URL path or query parameters on component mount
  useEffect(() => {
    let categoryFromUrl = params?.category
    
    // If no category in path, check query parameters
    if (!categoryFromUrl) {
      const urlParams = new URLSearchParams(window.location.search)
      categoryFromUrl = urlParams.get('category') || undefined
    }
    
    if (categoryFromUrl) {
      // Decode the category name (handle spaces and special characters)
      const decodedCategory = decodeURIComponent(categoryFromUrl)
      setCategoryFilter(decodedCategory)
    }
  }, [params?.category])

  // Update URL when category filter changes
  useEffect(() => {
    if (categoryFilter === 'all') {
      // Remove category from URL - go back to base /request-for-proposal-marketplace
      setLocation('/request-for-proposal-marketplace', { replace: true })
    } else {
      // Use path-based URL for category
      const encodedCategory = encodeURIComponent(categoryFilter)
      setLocation(`/request-for-proposal-marketplace/${encodedCategory}`, { replace: true })
    }
  }, [categoryFilter, setLocation])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Optimized batch watchlist status check with caching
  const checkWatchlistStatus = async (rfpIds: string[]) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      // Check if we have recent cache data (within 30 seconds)
      const now = Date.now()
      if (now - lastWatchlistCheck < 30000) {
        // Use cached data for RFPs we already know about
        const cachedStatus: Record<string, boolean> = {}
        let needsApiCall = false

        rfpIds.forEach(rfpId => {
          if (rfpId in watchlistCache) {
            cachedStatus[rfpId] = watchlistCache[rfpId]
          } else {
            needsApiCall = true
          }
        })

        if (!needsApiCall) {
          setWatchlistStatus(cachedStatus)
          return
        }
      }

      // Make batch API call
      const response = await fetch(buildApiUrl('/api/watchlist/check-batch'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rfpIds }),
      })

      if (response.ok) {
        const data = await response.json()
        const newStatusMap = data.data

        // Update cache with new data
        setWatchlistCache(prev => ({ ...prev, ...newStatusMap }))
        setWatchlistStatus(newStatusMap)
        setLastWatchlistCheck(now)
      } else if (response.status === 404) {
        // If batch endpoint is not available, skip watchlist checking
        console.warn('Watchlist batch endpoint not available, skipping watchlist status check')
        return
      }
    } catch (error) {
      console.error('Error checking watchlist status:', error)
    }
  }

  const addToWatchlist = async (rfpId: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to add items to your watchlist')
        return
      }

      const response = await fetch(buildApiUrl(`/api/watchlist/${rfpId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Update both state and cache
        setWatchlistStatus(prev => ({ ...prev, [rfpId]: true }))
        setWatchlistCache(prev => ({ ...prev, [rfpId]: true }))
        toast.success('Added to watchlist')
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to add to watchlist')
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error)
      toast.error('Failed to add to watchlist')
    }
  }

  const removeFromWatchlist = async (rfpId: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to add items to your watchlist')
        return
      }

      const response = await fetch(buildApiUrl(`/api/watchlist/${rfpId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Update both state and cache
        setWatchlistStatus(prev => ({ ...prev, [rfpId]: false }))
        setWatchlistCache(prev => ({ ...prev, [rfpId]: false }))
        toast.success('Removed from watchlist')
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to remove from watchlist')
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error)
      toast.error('Failed to remove from watchlist')
    }
  }

  useEffect(() => {
    const fetchRfps = async () => {
      try {
        setIsLoading(true)
        const token = localStorage.getItem('token')
        const endpoint = buildApiUrl('/api/rfps?')
        
        let url = endpoint
        if (statusFilter !== 'all') url += `status=${statusFilter}&`
        if (categoryFilter !== 'all') url += `category=${categoryFilter}&`
        if (debouncedSearchTerm) url += `search=${encodeURIComponent(debouncedSearchTerm)}&`

        const headers: Record<string, string> = {}
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch(url, { headers })

        if (response.ok) {
          const data = await response.json()
          setRfps(data.data)
          // Check watchlist status for all RFPs only if user is authenticated
          if (token && data.data.length > 0) {
            checkWatchlistStatus(data.data.map((rfp: Rfp) => rfp.id))
          }
        } else if (response.status === 401) {
          toast.error('Please log in to access additional features')
        } else {
          toast.error('Unable to load RFPs. Please try again.')
        }
      } catch (error) {
        console.error('Error fetching RFPs:', error)
        toast.error('Unable to load RFPs. Please check your connection and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    // Add a small delay to prevent rapid successive calls
    const timer = setTimeout(() => {
      fetchRfps()
    }, 100)

    return () => clearTimeout(timer)
  }, [debouncedSearchTerm, statusFilter, categoryFilter])



  const getCategoryColor = (category: string) => {
    switch (category) {
      // Technology
      case 'Technology':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'Software Development':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      case 'IT Services':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
      case 'Cybersecurity':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'Cloud Computing':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200'
      case 'Data Analytics':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'Artificial Intelligence':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      case 'Machine Learning':
        return 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200'
      case 'Blockchain':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'IoT':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      
      // Business
      case 'Business Strategy':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
      case 'Management Consulting':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      case 'Financial Services':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'Legal Services':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'Human Resources':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
      case 'Operations':
        return 'bg-stone-100 text-stone-800 dark:bg-stone-900 dark:text-stone-200'
      case 'Supply Chain':
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200'
      case 'Procurement':
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-900 dark:text-zinc-200'
      
      // Marketing & Design
      case 'Marketing':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      case 'Digital Marketing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'Content Creation':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      case 'Graphic Design':
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200'
      case 'Web Design':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'UI/UX Design':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
      case 'Branding':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
      case 'Social Media':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200'
      case 'SEO':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      case 'PPC':
        return 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200'
      
      // Creative & Media
      case 'Video Production':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'Photography':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'Animation':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'Audio Production':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'Podcasting':
        return 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200'
      case 'Live Streaming':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      
      // Education & Training
      case 'Training':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'E-learning':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      case 'Educational Content':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'Workshop Development':
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200'
      
      // Health & Wellness
      case 'Healthcare':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'Fitness':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'Nutrition':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      case 'Mental Health':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      case 'Wellness Programs':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
      
      // Real Estate
      case 'Real Estate':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'Property Management':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'Construction':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'Architecture':
        return 'bg-stone-100 text-stone-800 dark:bg-stone-900 dark:text-stone-200'
      case 'Interior Design':
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200'
      
      // Other
      case 'Event Planning':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'Travel Services':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200'
      case 'Translation':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      case 'Research':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
      case 'Environmental':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      case 'Non-profit':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
      case 'Government':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      case 'Other':
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-900 dark:text-zinc-200'
      
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      // Technology
      case 'Technology':
        return <Zap className="h-3 w-3" />
      case 'Software Development':
        return <Code className="h-3 w-3" />
      case 'IT Services':
        return <Settings className="h-3 w-3" />
      case 'Cybersecurity':
        return <Shield className="h-3 w-3" />
      case 'Cloud Computing':
        return <Cloud className="h-3 w-3" />
      case 'Data Analytics':
        return <Database className="h-3 w-3" />
      case 'Artificial Intelligence':
        return <Brain className="h-3 w-3" />
      case 'Machine Learning':
        return <BrainCircuit className="h-3 w-3" />
      case 'Blockchain':
        return <LinkIcon className="h-3 w-3" />
      case 'IoT':
        return <Smartphone className="h-3 w-3" />
      
      // Business
      case 'Business Strategy':
        return <Target className="h-3 w-3" />
      case 'Management Consulting':
        return <Users className="h-3 w-3" />
      case 'Financial Services':
        return <TrendingUp className="h-3 w-3" />
      case 'Legal Services':
        return <Scale className="h-3 w-3" />
      case 'Human Resources':
        return <UserCheck className="h-3 w-3" />
      case 'Operations':
        return <Settings className="h-3 w-3" />
      case 'Supply Chain':
        return <Truck className="h-3 w-3" />
      case 'Procurement':
        return <ShoppingCart className="h-3 w-3" />
      
      // Marketing & Design
      case 'Marketing':
        return <Megaphone className="h-3 w-3" />
      case 'Digital Marketing':
        return <Globe className="h-3 w-3" />
      case 'Content Creation':
        return <PenTool className="h-3 w-3" />
      case 'Graphic Design':
        return <Palette className="h-3 w-3" />
      case 'Web Design':
        return <Monitor className="h-3 w-3" />
      case 'UI/UX Design':
        return <MousePointer className="h-3 w-3" />
      case 'Branding':
        return <Eye className="h-3 w-3" />
      case 'Social Media':
        return <Share2 className="h-3 w-3" />
      case 'SEO':
        return <SearchIcon className="h-3 w-3" />
      case 'PPC':
        return <Target className="h-3 w-3" />
      
      // Creative & Media
      case 'Video Production':
        return <Video className="h-3 w-3" />
      case 'Photography':
        return <Camera className="h-3 w-3" />
      case 'Animation':
        return <Zap className="h-3 w-3" />
      case 'Audio Production':
        return <Music className="h-3 w-3" />
      case 'Podcasting':
        return <Mic className="h-3 w-3" />
      case 'Live Streaming':
        return <Radio className="h-3 w-3" />
      
      // Education & Training
      case 'Training':
        return <GraduationCap className="h-3 w-3" />
      case 'E-learning':
        return <BookOpen className="h-3 w-3" />
      case 'Educational Content':
        return <School className="h-3 w-3" />
      case 'Workshop Development':
        return <FileBox className="h-3 w-3" />
      
      // Health & Wellness
      case 'Healthcare':
        return <Stethoscope className="h-3 w-3" />
      case 'Fitness':
        return <Dumbbell className="h-3 w-3" />
      case 'Nutrition':
        return <Apple className="h-3 w-3" />
      case 'Mental Health':
        return <Heart className="h-3 w-3" />
      case 'Wellness Programs':
        return <Heart className="h-3 w-3" />
      
      // Real Estate
      case 'Real Estate':
        return <Home className="h-3 w-3" />
      case 'Property Management':
        return <Building2 className="h-3 w-3" />
      case 'Construction':
        return <Hammer className="h-3 w-3" />
      case 'Architecture':
        return <Ruler className="h-3 w-3" />
      case 'Interior Design':
        return <Sofa className="h-3 w-3" />
      
      // Other
      case 'Event Planning':
        return <Calendar className="h-3 w-3" />
      case 'Travel Services':
        return <Plane className="h-3 w-3" />
      case 'Translation':
        return <Languages className="h-3 w-3" />
      case 'Research':
        return <SearchIcon className="h-3 w-3" />
      case 'Environmental':
        return <Leaf className="h-3 w-3" />
      case 'Non-profit':
        return <HeartHandshake className="h-3 w-3" />
      case 'Government':
        return <BuildingIcon className="h-3 w-3" />
      case 'Other':
        return <Globe className="h-3 w-3" />
      
      default:
        return <Building className="h-3 w-3" />
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">RFP and RFQ Marketplace</h1>
            <p className="text-muted-foreground">
              Browse and discover Request for Proposals and Request for Quotes from various companies.
            </p>
          </div>
          {user && (
            <Link href="/create">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create RFP
              </Button>
            </Link>
          )}
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading RFPs...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">RFP and RFQ Marketplace</h1>
          <p className="text-muted-foreground">
            Browse and discover Request for Proposals and Request for Quotes from various companies.
          </p>
        </div>
        {user && (
          <Link href="/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create RFP
            </Button>
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
          <CardDescription>
            Find specific RFPs or filter by status, category, or date range.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search RFPs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm"
            >
              <option value="all">All Status</option>
              <option value="PUBLISHED">Published</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm"
            >
              <option value="all">All Categories</option>
              <optgroup label="Technology">
                <option value="Technology">Technology</option>
                <option value="Software Development">Software Development</option>
                <option value="IT Services">IT Services</option>
                <option value="Cybersecurity">Cybersecurity</option>
                <option value="Cloud Computing">Cloud Computing</option>
                <option value="Data Analytics">Data Analytics</option>
                <option value="Artificial Intelligence">Artificial Intelligence</option>
                <option value="Machine Learning">Machine Learning</option>
                <option value="Blockchain">Blockchain</option>
                <option value="IoT">IoT</option>
              </optgroup>
              <optgroup label="Business">
                <option value="Business Strategy">Business Strategy</option>
                <option value="Management Consulting">Management Consulting</option>
                <option value="Financial Services">Financial Services</option>
                <option value="Legal Services">Legal Services</option>
                <option value="Human Resources">Human Resources</option>
                <option value="Operations">Operations</option>
                <option value="Supply Chain">Supply Chain</option>
                <option value="Procurement">Procurement</option>
              </optgroup>
              <optgroup label="Marketing & Design">
                <option value="Marketing">Marketing</option>
                <option value="Digital Marketing">Digital Marketing</option>
                <option value="Content Creation">Content Creation</option>
                <option value="Graphic Design">Graphic Design</option>
                <option value="Web Design">Web Design</option>
                <option value="UI/UX Design">UI/UX Design</option>
                <option value="Branding">Branding</option>
                <option value="Social Media">Social Media</option>
                <option value="SEO">SEO</option>
                <option value="PPC">PPC</option>
              </optgroup>
              <optgroup label="Creative & Media">
                <option value="Video Production">Video Production</option>
                <option value="Photography">Photography</option>
                <option value="Animation">Animation</option>
                <option value="Audio Production">Audio Production</option>
                <option value="Podcasting">Podcasting</option>
                <option value="Live Streaming">Live Streaming</option>
              </optgroup>
              <optgroup label="Education & Training">
                <option value="Training">Training</option>
                <option value="E-learning">E-learning</option>
                <option value="Educational Content">Educational Content</option>
                <option value="Workshop Development">Workshop Development</option>
              </optgroup>
              <optgroup label="Health & Wellness">
                <option value="Healthcare">Healthcare</option>
                <option value="Fitness">Fitness</option>
                <option value="Nutrition">Nutrition</option>
                <option value="Mental Health">Mental Health</option>
                <option value="Wellness Programs">Wellness Programs</option>
              </optgroup>
              <optgroup label="Real Estate">
                <option value="Real Estate">Real Estate</option>
                <option value="Property Management">Property Management</option>
                <option value="Construction">Construction</option>
                <option value="Architecture">Architecture</option>
                <option value="Interior Design">Interior Design</option>
              </optgroup>
              <optgroup label="Other">
                <option value="Event Planning">Event Planning</option>
                <option value="Travel Services">Travel Services</option>
                <option value="Translation">Translation</option>
                <option value="Research">Research</option>
                <option value="Environmental">Environmental</option>
                <option value="Non-profit">Non-profit</option>
                <option value="Government">Government</option>
                <option value="Other">Other</option>
              </optgroup>
            </select>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
                setCategoryFilter('all')
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Popular Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Popular Categories</CardTitle>
          <CardDescription>
            Quick filter by popular categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {['Technology', 'Web Design', 'Marketing', 'Business Strategy', 'Software Development', 'Digital Marketing'].map((category) => (
              <button
                key={category}
                onClick={() => setCategoryFilter(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  categoryFilter === category
                    ? 'bg-primary text-primary-foreground'
                    : `${getCategoryColor(category)} hover:opacity-80`
                }`}
              >
                {getCategoryIcon(category)}
                {category}
              </button>
            ))}
            <button
              onClick={() => setCategoryFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                categoryFilter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All Categories
            </button>
          </div>
        </CardContent>
      </Card>

      {/* RFP List */}
      <div className="space-y-4">
        {rfps.map((rfp) => (
          <Card key={rfp.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1 min-w-0">
                  <div className="h-12 w-12 rounded-lg gradient-bg flex items-center justify-center flex-shrink-0">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center space-x-2 mb-2">
                      <Link href={`/rfp/${rfp.slug}`} className="hover:underline min-w-0 flex-1">
                        <h3 className="text-lg font-semibold truncate text-primary hover:text-primary/80 transition-colors">
                          {rfp.title}
                        </h3>
                      </Link>
                      <span 
                        className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${getCategoryColor(rfp.category)}`}
                        onClick={() => setCategoryFilter(rfp.category)}
                        title={`Filter by ${rfp.category}`}
                      >
                        {getCategoryIcon(rfp.category)}
                        {rfp.category}
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-3 line-clamp-2 overflow-hidden text-ellipsis break-words">
                      {rfp.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span>Budget: ${rfp.budgetMin.toLocaleString()} - ${rfp.budgetMax.toLocaleString()} {rfp.budgetCurrency}</span>
                      <span>Deadline: {new Date(rfp.deadline).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2 ml-4">
                  <div className="text-right">
                    <div className="text-sm font-medium">{rfp.views} views</div>
                    <div className="text-sm text-muted-foreground">
                      {rfp.responses} responses
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {rfp.linkedinShares} shares
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {user && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => watchlistStatus[rfp.id] 
                          ? removeFromWatchlist(rfp.id) 
                          : addToWatchlist(rfp.id)
                        }
                        className={watchlistStatus[rfp.id] ? 'text-primary' : ''}
                      >
                        {watchlistStatus[rfp.id] ? (
                          <BookmarkCheck className="h-4 w-4" />
                        ) : (
                          <Bookmark className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Link href={`/rfp/${rfp.slug}`}>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {rfps.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No RFPs found</h3>
            <p className="text-muted-foreground mb-4">
              {user ? 'Get started by creating your first RFP.' : 'No RFPs are currently available. Check back later!'}
            </p>
            {user && (
              <Link href="/create">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First RFP
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
} 